// Online play's wire format (2026-11-01, Devin: "make this multiplayer as soon as possible"). Lockstep: every player's
// device runs the same deterministic sim (src/sim, its math in dmath.ts), and only commands travel. The room server
// keeps the clock: every TURN_MS it sends turn t, the commands it took since the last turn; each device applies turn t
// right before sim step t, so every device steps through the same match. Bots take every seat no person holds, on every
// device alike, so they need no traffic either. JSON, since a turn is a few bytes: most are { k: 't', t }.
// Pure: no DOM, no I/O. The room (room.ts), the Cloudflare worker (worker/rooms.ts) and the client (client.ts) share it.
import { CLASSES, FACTIONS, HEROES, ITEMS, type ClassId, type FactionId, type HeroId, type ItemId } from '../sim/defs';
import { CAP, GROUPS, PLAYERS, SIZE, type Command, type Goal } from '../sim/sim';

/** One sim step is 50 ms (DT, 20 Hz); the room sends one turn per step. */
export const TURN_MS = 50;
/** Every this many ticks each device reports its sim's hash, so a desync is seen (the sim is meant never to desync). */
export const HASH_EVERY = 100;
/** A dropped player's seat waits this long for them before a bot takes it over. As long as the room waits for a hello
 *  (worker/rooms.ts HELLO_MS is this): a slow phone reloading spends most of it compiling shaders, and a shorter grace
 *  let a bot spend its gold and pick its army first (Flash re-check F4). */
export const GRACE_MS = 45_000;
/** A quick-match room starts this long after its first player joins, or at once when every seat is a person. */
export const QUICK_WAIT_MS = 20_000;
/** Commands one player may send per second (a tap is one; a drag-select order is one). Over it they are dropped. */
export const CMD_RATE = 12;
/** A name, as shown on the seat: printable, trimmed, at most this long. */
export const NAME_MAX = 20;
/** Room codes: no 0/O, 1/I/L, so one read aloud is typed right. */
export const CODE_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789', CODE_LEN = 5;

export type Seat = { name: string; human: boolean; online: boolean; hero: HeroId | null };
export type Phase = 'lobby' | 'playing' | 'over';
/** A seat changing hands mid-match, applied at the start of its turn on every device. */
export type SeatEvent = { p: number; to: 'bot' } | { p: number; to: 'human'; name: string };
export type Turn = { t: number; c?: [number, Command][]; e?: SeatEvent[] };
export type Start = { seed: number; seats: Seat[]; quick: boolean };

export type ToServer =
  /** `build`: the page's net build (scripts/net-build.ts); a room takes one. */
  | { k: 'hello'; name: string; token?: string; have?: number; build?: string }
  | { k: 'hero'; id: HeroId }
  | { k: 'start' }
  | { k: 'cmd'; c: Command }
  | { k: 'hash'; t: number; h: string }
  | { k: 'leave' };
export type ToClient =
  /** `now` is the room's clock as it sent this, so a page can turn startsAt into a countdown on its own clock. */
  | { k: 'room'; code: string; you: number; token: string; host: number; phase: Phase; seats: Seat[]; quick: boolean; now: number; startsAt?: number }
  /** The match: its seed and seats at tick 0, and every turn so far (from `have` on a reconnect), so a late or returning
   *  device fast-forwards to the present. `turns` holds only those that carried something; every turn below `at` has
   *  been sent, so the ones missing between `from` and `at` were empty. */
  | { k: 'start'; start: Start; turns: Turn[]; from: number; at: number }
  | { k: 't'; t: number; c?: [number, Command][]; e?: SeatEvent[] }
  | { k: 'desync'; t: number }
  | { k: 'err'; m: string };

const isInt = (v: unknown, lo: number, hi: number): v is number => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;
const isNum = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
const oneOf = <T extends string>(v: unknown, list: readonly T[]): v is T => typeof v === 'string' && (list as readonly string[]).includes(v);

function cleanGoal(g: unknown): Goal | null | undefined {
  if (g === null) return null;
  if (!g || typeof g !== 'object') return undefined;
  const o = g as Record<string, unknown>;
  if (o.kind === 'center') return { kind: 'center' };
  if (o.kind === 'base' && isInt(o.p, 0, PLAYERS - 1)) return { kind: 'base', p: o.p };
  if (o.kind === 'point' && isNum(o.x, 0, SIZE) && isNum(o.y, 0, SIZE)) return { kind: 'point', x: o.x, y: o.y };
  return undefined;
}
const cleanIds = (v: unknown): number[] | null =>
  Array.isArray(v) && v.length <= CAP && v.every((n) => isInt(n, 0, 0x7fffffff)) ? [...new Set(v as number[])] : null;

/** A command rebuilt from only the fields the sim reads, every number finite and in range, or null. The room runs it on
 *  what a player sends and every device runs it again on what a turn carries, so a bad message can't end a match with
 *  a NaN in everyone's sim. The sim still refuses a well-formed command it can't take (not enough gold, say). */
export function cleanCommand(raw: unknown): Command | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  switch (c.t) {
    case 'tier': return c.faction === undefined ? { t: 'tier' } : oneOf<FactionId>(c.faction, FACTIONS) ? { t: 'tier', faction: c.faction } : null;
    case 'slot': return isInt(c.i, 0, 9) && oneOf<ClassId>(c.cls, CLASSES) ? { t: 'slot', i: c.i, cls: c.cls } : null;
    case 'goal': { const g = cleanGoal(c.goal); return g ? (c.back === true ? { t: 'goal', goal: g, back: true } : { t: 'goal', goal: g }) : null; }
    case 'order': {
      const ids = cleanIds(c.ids), g = cleanGoal(c.goal);
      if (!ids || g === undefined) return null;
      const out: Extract<Command, { t: 'order' }> = { t: 'order', ids, goal: g };
      if (c.move === true) out.move = true;
      if (c.tap !== undefined) { const tp = c.tap as Record<string, unknown> | null; if (!tp || !isNum(tp.x, 0, SIZE) || !isNum(tp.y, 0, SIZE)) return null; out.tap = { x: tp.x, y: tp.y }; }
      return out;
    }
    case 'group': {
      const ids = cleanIds(c.ids);
      if (!ids || !isInt(c.k, 0, GROUPS - 1)) return null;
      return c.add === true ? { t: 'group', k: c.k, ids, add: true } : { t: 'group', k: c.k, ids };
    }
    case 'rally': return isInt(c.k, -1, GROUPS - 1) ? { t: 'rally', k: c.k } : null;
    case 'hero': return oneOf<HeroId>(c.id, HEROES) ? { t: 'hero', id: c.id } : null;
    case 'cast': return c.k === 0 || c.k === 1 ? { t: 'cast', k: c.k } : null;
    case 'buy': return oneOf<ItemId>(c.item, ITEMS) ? { t: 'buy', item: c.item } : null;
    case 'extra': return { t: 'extra' };
    default: return null;
  }
}

/** A seat name: printable characters only, spaces squeezed, trimmed to NAME_MAX; empty becomes `fallback`. */
export function cleanName(v: unknown, fallback: string): string {
  const s = typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g, '').replace(/\s+/g, ' ').trim() : '';
  return [...s].slice(0, NAME_MAX).join('') || fallback;
}

/** A net build as scripts/net-build.ts prints it (or 'dev' outside a build). */
export const isBuild = (v: unknown): v is string => typeof v === 'string' && /^[a-z0-9]{1,16}$/.test(v);
export const isCode = (v: unknown): v is string => typeof v === 'string' && v.length === CODE_LEN && [...v].every((ch) => CODE_CHARS.includes(ch));
export function newCode(rand: () => number): string {
  let s = '';
  for (let i = 0; i < CODE_LEN; i++) s += CODE_CHARS[Math.floor(rand() * CODE_CHARS.length)];
  return s;
}

/** A message from a player, or null when it isn't one of ToServer's shapes. */
export function parseToServer(text: unknown): ToServer | null {
  if (typeof text !== 'string' || text.length > 16_384) return null;
  let m: Record<string, unknown>;
  try { m = JSON.parse(text); } catch { return null; }
  if (!m || typeof m !== 'object') return null;
  switch (m.k) {
    case 'hello': return { k: 'hello', name: cleanName(m.name, ''), ...(typeof m.token === 'string' && m.token.length <= 64 ? { token: m.token } : {}), ...(isInt(m.have, 0, 1e7) ? { have: m.have } : {}), ...(isBuild(m.build) ? { build: m.build } : {}) };
    case 'hero': return oneOf<HeroId>(m.id, HEROES) ? { k: 'hero', id: m.id } : null;
    case 'start': return { k: 'start' };
    case 'leave': return { k: 'leave' };
    case 'cmd': { const c = cleanCommand(m.c); return c ? { k: 'cmd', c } : null; }
    case 'hash': return isInt(m.t, 0, 1e7) && typeof m.h === 'string' && m.h.length <= 16 ? { k: 'hash', t: m.t, h: m.h } : null;
    default: return null;
  }
}
